import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import useDestinations from "@/hooks/useDestinations";
import { DEST } from "@/constants/testIds";
import DestinationBanner from "@/components/destinations/DestinationBanner";
import DestinationBody from "@/components/destinations/DestinationBody";
import DestinationGallery from "@/components/destinations/DestinationGallery";

const HASH_SCROLL_DELAY_MS = 300;

export default function Destinations() {
  const items = useDestinations();
  const { hash } = useLocation();

  useEffect(() => {
    if (!hash || items.length === 0) return;
    // `el`, `id`, `timer` are intentionally local to this effect and not
    // dependencies — they are derived from `hash` & DOM state each time.
    const id = hash.replace("#", "");
    const el = document.getElementById(id);
    if (!el) return;
    const timer = setTimeout(
      () => el.scrollIntoView({ behavior: "smooth" }),
      HASH_SCROLL_DELAY_MS
    );
    return () => clearTimeout(timer);
  }, [hash, items]);

  return (
    <main className="bg-ivory text-ink">
      <DestinationsHeader />
      {items.map((d, idx) => (
        <DestinationSection key={d.slug} d={d} idx={idx} />
      ))}
    </main>
  );
}

function DestinationsHeader() {
  return (
    <section className="pt-40 md:pt-52 pb-24 md:pb-32 max-w-[1480px] mx-auto px-6 md:px-12">
      <div className="eyebrow mb-8">Our Compass</div>
      <h1
        data-testid={DEST.pageTitle}
        className="font-serif text-6xl md:text-[9rem] leading-[0.9] tracking-tighter font-light"
      >
        Four <em className="italic text-indigo_deep">lands.</em>
        <br />
        Each a different silence.
      </h1>
      <p className="mt-12 max-w-2xl text-lg text-ink/70 font-light leading-relaxed">
        Travel with us is unhurried, intimate, and intentionally selective.
        Our region of devotion runs from the Himalayas through the
        subcontinent and across the Palk Strait — a single cultural compass
        of four extraordinary worlds.
      </p>
    </section>
  );
}

function DestinationSection({ d, idx }) {
  const isEven = idx % 2 === 0;
  return (
    <section
      id={d.slug}
      data-testid={DEST.sectionAnchor(d.slug)}
      className={`relative py-24 md:py-32 ${isEven ? "bg-ivory" : "bg-ivory-alt"}`}
    >
      <div className="max-w-[1480px] mx-auto px-6 md:px-12">
        <DestinationBanner d={d} idx={idx} />
        <DestinationBody d={d} />
        <DestinationGallery d={d} />
      </div>
    </section>
  );
}
